class FacetFiltersForm extends HTMLElement {
  /**
   * The constructor function is called when the component is created. It's used to set up the initial
   * state of the component
   */
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * It adds an event listener to the window object that listens for the popstate event, and when it
   * occurs, it calls the onHistoryChange function
   */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  /**
   * It toggles the disabled class on all elements with the class js-facet-remove
   * @param [disable=true] - boolean
   */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * It loops through the sections and creates a url for each section
   * @param searchParams - The search parameters that are passed to the function.
   * @param event - The event that triggered the renderPage function.
   * @param [updateURLHash=true] - This is a boolean value that determines whether or not the URL hash
   * should be updated.
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    /* Checking if the countContainer exists and if it does, it adds the loading class to it. */
    if (countContainer){
      countContainer.classList.add('loading');
    }
    /* Checking if the countContainerDesktop exists and if it does, it adds the loading class to it. */
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    /* Looping through the sections and creating a url for each section. */
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    /* Updating the URL hash. */
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  /**
   * Fetching the url and then it is rendering the html, event, and url
   * @param url - The url that is being fetched.
   * @param event - The event that triggered the function.
   */
  static renderSectionFromFetch(url, event) {
    /* Fetching the url and then it is rendering the html, event, and url. */
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  /**
   * If the filter data is in the cache, render the filters, product grid container, and product count
   * from the cached data
   * @param filterDataUrl - The URL of the page that contains the filters.
   * @param event - The event that triggered the request.
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  /**
   * It takes the HTML of a page, and replaces the HTML of the element with the ID of
   * "ProductGridContainer" with the HTML of the element with the ID of "ProductGridContainer" from the
   * HTML that was passed in
   * @param html - The HTML string that contains the product grid.
   */
  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  /**
   * It takes the HTML of the page, parses it, and then replaces the contents of the element with the
   * id of "ProductCount" with the contents of the element with the id of "ProductCount" in the HTML
   * @param html - The HTML returned from the AJAX request.
   */
  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  /**
   * It takes the HTML returned from the server, parses it, and then updates the DOM with the new HTML
   * @param html - The HTML returned from the server.
   * @param event - The event that triggered the AJAX request.
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  /**
   * It takes the HTML from the AJAX response and injects it into the DOM
   * @param html - The HTML returned from the AJAX request.
   */
  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * It takes the HTML from the AJAX response and replaces the HTML of the elements that are not part
   * of the product list
   * @param html - The HTML returned from the server.
   */
  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  /**
   * It takes the HTML from the source element and replaces the HTML of the target element
   * @param source - The source element that contains the HTML to be copied.
   * @param target - The element that will be updated with the new content.
   */
  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  /**
   * It takes a string of search parameters, and updates the URL hash to include those parameters
   * @param searchParams - The search parameters to be added to the URL.
   */
  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * It returns an array of objects, each object containing a section property that is the value of the
   * data-id attribute of the element with the id of product-grid
   * @returns An array of objects.
   */
  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  /**
   * It takes a form element and returns a string of the form's data in the format of a query string
   * @param form - The form element that contains the search parameters.
   * @returns A string of the form data.
   */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  /**
   * The function takes the searchParams object and the event object as arguments, and then calls the
   * renderPage function in the FacetFiltersForm class, passing it the searchParams object and the
   * event object
   * @param searchParams - The search parameters that are passed to the search API.
   * @param event - The event that triggered the form submission.
   */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /**
   * If the event is a checkbox, then create a search param for the form that the checkbox is in. If
   * the event is a button, then create a search param for the form that the button is in
   * @param event - The event that triggered the submit.
   */
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  /**
   * It takes the URL of the link that was clicked, and then it renders the page with the new URL
   * @param event - The event object that was triggered.
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* It listens for changes to the input elements, and when a change occurs, it adjusts the min and max
attributes of the input elements to ensure that the values are valid */
class PriceRange extends HTMLElement {
  /**
   * The constructor function is called when the component is created. It sets up the event listeners
   * for the range inputs and calls the setMinAndMaxValues function
   */
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  /**
   * The function adjusts the values of the range inputs to valid values, and then sets the min and max
   * values of the range inputs
   * @param event - The event object that was triggered by the user.
   */
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  /**
   * If the max input has a value, set the min input's max attribute to that value. If the min input
   * has a value, set the max input's min attribute to that value. If the min input has no value, set
   * the max input's min attribute to 0. If the max input has no value, set the min input's max
   * attribute to the max input's max attribute
   */
  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  /**
   * If the value is less than the minimum, set the value to the minimum. If the value is greater than
   * the maximum, set the value to the maximum
   * @param input - The input element that the user is typing in.
   */
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

/* It adds a click event listener to the link inside the facet-remove element. When the link is
clicked, it calls the onActiveFilterClick method on the facet-filters-form element */
class FacetRemove extends HTMLElement {
  /**
   * The function is called when the component is created. It adds a click event listener to the link
   * that closes the filter. It also adds a keyup event listener to the link that closes the filter
   * when the space bar is pressed
   */
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  /**
   * The function is called when the user clicks on the "X" button to close a filter. The function
   * prevents the default action of the event, then finds the closest form element and calls the
   * onActiveFilterClick function
   * @param event - The event that triggered the function.
   */
  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
